/**
 * VendorPage.jsx
 * Vendor-level aggregation report — revenue, inventory, and stock health
 * metrics grouped by vendor/supplier.
 */

import React, { useState } from 'react';
import {
  Card,
  DataTable,
  SkeletonBodyText,
  Banner,
  EmptyState,
  Text,
  BlockStack,
  Badge,
  Pagination,
  Select,
  InlineStack,
  TextField,
  Button,
} from '@shopify/polaris';

import { useReport } from '../hooks/useReport';
import { useExport } from '../hooks/useExport';
import { api } from '../api/api';
import {
  formatCurrency,
  formatNumber,
  periodLabel,
} from '../utils/formatters';

const ROWS_PER_PAGE = 50;

const SORT_OPTIONS = [
  { label: 'Revenue (high → low)', value: 'totalRevenue:desc' },
  { label: 'Revenue (low → high)', value: 'totalRevenue:asc' },
  { label: 'Units sold (high → low)', value: 'totalUnitsSold:desc' },
  { label: 'SKU count (high → low)', value: 'variantCount:desc' },
  { label: 'Dead stock count (high → low)', value: 'deadStockCount:desc' },
  { label: 'Overstock value (high → low)', value: 'totalOverstockValue:desc' },
  { label: 'Vendor A → Z', value: 'vendor:asc' },
];

const VENDOR_CSV_COLUMNS = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'totalRevenue', label: 'Revenue' },
  { key: 'totalUnitsSold', label: 'Units Sold' },
  { key: 'variantCount', label: 'SKU Count' },
  { key: 'totalInventory', label: 'Total Inventory' },
  { key: 'deadStockCount', label: 'Dead Stock Count' },
  { key: 'overstockedCount', label: 'Overstock Count' },
  { key: 'totalOverstockValue', label: 'Overstock Value' },
  { key: 'avgDailySellRate', label: 'Avg Daily Sell Rate' },
];

/**
 * VendorPage — vendor-level aggregation report.
 *
 * @param {{ period: string, overstockDays: number, filterBar: JSX.Element }} props
 */
export function VendorPage({ period, overstockDays, filterBar }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('totalRevenue:desc');
  const [search, setSearch] = useState('');

  const [sortBy, sortDir] = sort.split(':');

  const { data, loading, error } = useReport(api.getByVendor, {
    period,
    overstockDays,
    page,
    limit: ROWS_PER_PAGE,
    sortBy,
    sortDir,
  });

  const csvFilename = `vendor-report-${period}-${new Date().toISOString().slice(0, 10)}.csv`;
  const { exporting, handleExport } = useExport(
    api.getByVendor,
    { period, overstockDays, sortBy, sortDir },
    VENDOR_CSV_COLUMNS,
    csvFilename,
  );

  // Client-side search filter on vendor name
  const allRows = data?.data || [];
  const filteredRows = search
    ? allRows.filter((r) => r.vendor.toLowerCase().includes(search.toLowerCase()))
    : allRows;

  // Summary cards
  const totalVendors = data?.pagination?.total || 0;
  const totalRevenue = filteredRows.reduce((sum, r) => sum + r.totalRevenue, 0);
  const vendorsWithDeadStock = filteredRows.filter((r) => r.deadStockCount > 0).length;

  const tableRows = filteredRows.map((row) => [
    <Text fontWeight="semibold" as="span">{row.vendor}</Text>,
    <Text fontWeight="semibold" as="span">{formatCurrency(row.totalRevenue)}</Text>,
    formatNumber(row.totalUnitsSold),
    formatNumber(row.variantCount),
    row.deadStockCount > 0
      ? <Badge tone="critical">{row.deadStockCount}</Badge>
      : '0',
    row.overstockedCount > 0
      ? <Badge tone="attention">{row.overstockedCount}</Badge>
      : '0',
    row.totalOverstockValue > 0
      ? <Text tone="caution" as="span">{formatCurrency(row.totalOverstockValue)}</Text>
      : '—',
  ]);

  const hasData = !loading && !error;
  const isEmpty = hasData && filteredRows.length === 0;

  return (
    <BlockStack gap="400">
      <Card>{filterBar}</Card>

      {/* Summary cards */}
      {hasData && filteredRows.length > 0 && (
        <InlineStack gap="400" wrap>
          <Card>
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="p">Total Vendors</Text>
              <Text variant="heading2xl" as="p">{formatNumber(totalVendors)}</Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="p">Total Revenue</Text>
              <Text variant="heading2xl" as="p">{formatCurrency(totalRevenue)}</Text>
            </BlockStack>
          </Card>
          <Card>
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="p">Vendors with Dead Stock</Text>
              <Text variant="heading2xl" as="p" tone={vendorsWithDeadStock > 0 ? 'critical' : undefined}>
                {formatNumber(vendorsWithDeadStock)}
              </Text>
            </BlockStack>
          </Card>
        </InlineStack>
      )}

      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd" as="h2">
              Vendor Report — {periodLabel(period)}
            </Text>
            <InlineStack gap="300" blockAlign="center">
              {data?.pagination && (
                <Text variant="bodySm" tone="subdued" as="span">
                  {formatNumber(data.pagination.total)} vendors
                </Text>
              )}
              <Button
                size="slim"
                onClick={handleExport}
                loading={exporting}
                disabled={loading || !data?.data?.length}
              >
                Export CSV
              </Button>
            </InlineStack>
          </InlineStack>

          <InlineStack gap="300" wrap>
            <div style={{ flex: 1, minWidth: 220 }}>
              <TextField
                label="" labelHidden
                placeholder="Search vendor…"
                value={search}
                onChange={(v) => { setSearch(v); setPage(1); }}
                clearButton
                onClearButtonClick={() => { setSearch(''); setPage(1); }}
                autoComplete="off"
              />
            </div>
            <div style={{ minWidth: 240 }}>
              <Select
                label="" labelHidden
                options={SORT_OPTIONS}
                value={sort}
                onChange={(v) => { setSort(v); setPage(1); }}
              />
            </div>
          </InlineStack>

          {error && (
            <Banner title="Error loading vendor data" tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {loading && <SkeletonBodyText lines={12} />}

          {isEmpty && (
            <EmptyState
              heading="No vendor data available"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>No products found for this period. Try selecting a longer time range.</p>
            </EmptyState>
          )}

          {hasData && filteredRows.length > 0 && (
            <>
              <DataTable
                columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'text', 'text', 'numeric']}
                headings={[
                  'Vendor', 'Revenue', 'Units Sold', 'SKU Count',
                  'Dead Stock', 'Overstocked', 'Overstock Value',
                ]}
                rows={tableRows}
                footerContent={`Page ${data?.pagination?.page} of ${data?.pagination?.totalPages}`}
              />
              <Pagination
                hasPrevious={page > 1}
                hasNext={page < (data?.pagination?.totalPages || 1)}
                onPrevious={() => setPage((p) => p - 1)}
                onNext={() => setPage((p) => p + 1)}
                label={`Page ${page} of ${data?.pagination?.totalPages || 1}`}
              />
            </>
          )}
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
